import { Alert, Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import type {
  AudienceStaffOptionDto,
  DesktopNotificationType,
} from '@/api/types/desktopNotification.types';
import { useAudienceStaffOptions } from '@/queries/useDesktopNotifications';
import { useGeos } from '@/queries/useLookups';

const GRADES = ['AC', 'C', 'M', 'AP', 'P'] as const;

interface AudienceValue {
  grades: string[];
  geos: string[];
  staffIds: number[];
  excludedStaffIds: number[];
  completionRateMinPercent: number | null;
  completionRateMaxPercent: number | null;
}

interface AudienceFilterSelectorProps {
  value: AudienceValue;
  type: DesktopNotificationType | null;
  onChange: (next: AudienceValue) => void;
}

function getStaffLabel(option: AudienceStaffOptionDto): string {
  return `${option.displayName} (${option.office})`;
}

export function AudienceFilterSelector({ value, type, onChange }: AudienceFilterSelectorProps) {
  const { data: geos = [] } = useGeos();
  const { data: staffOptions = [] } = useAudienceStaffOptions(type);

  // Completion rate is a happiness-survey concept, and the server rejects the bounds on any other type.
  const showCompletionRate = type === 'HappinessSurvey';

  const selectedStaff = staffOptions.filter((opt) => value.staffIds.includes(opt.staffId));
  const selectedExcludedStaff = staffOptions.filter((opt) =>
    value.excludedStaffIds.includes(opt.staffId),
  );

  const isEmpty =
    value.grades.length === 0 && value.geos.length === 0 && value.staffIds.length === 0;

  const handleRateChange =
    (key: 'completionRateMinPercent' | 'completionRateMaxPercent') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') {
        onChange({ ...value, [key]: null });
        return;
      }
      const parsed = Number(raw);
      onChange({ ...value, [key]: Number.isFinite(parsed) ? parsed : null });
    };

  const handleRateBlur =
    (key: 'completionRateMinPercent' | 'completionRateMaxPercent') => () => {
      const current = value[key];
      if (current === null) return;
      const clamped = Math.min(100, Math.max(0, current));
      if (clamped !== current) {
        onChange({ ...value, [key]: clamped });
      }
    };

  return (
    <Stack spacing={2}>
      <Autocomplete
        multiple
        size="small"
        options={[...GRADES]}
        value={value.grades}
        onChange={(_, newValue) => onChange({ ...value, grades: newValue })}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Grades"
            placeholder="Select grades"
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Grades',
            }}
          />
        )}
      />

      <Autocomplete
        multiple
        size="small"
        options={geos}
        value={value.geos}
        onChange={(_, newValue) => onChange({ ...value, geos: newValue })}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Geos"
            placeholder="Select geos"
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Geos',
            }}
          />
        )}
      />

      <Autocomplete
        multiple
        size="small"
        options={staffOptions}
        value={selectedStaff}
        getOptionLabel={getStaffLabel}
        isOptionEqualToValue={(opt, val) => opt.staffId === val.staffId}
        onChange={(_, newValue) =>
          onChange({ ...value, staffIds: newValue.map((opt) => opt.staffId) })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Staff"
            placeholder="Select staff"
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Staff',
            }}
          />
        )}
      />

      <Autocomplete
        multiple
        size="small"
        options={staffOptions}
        value={selectedExcludedStaff}
        getOptionLabel={getStaffLabel}
        isOptionEqualToValue={(opt, val) => opt.staffId === val.staffId}
        onChange={(_, newValue) =>
          onChange({ ...value, excludedStaffIds: newValue.map((opt) => opt.staffId) })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Exclude staff"
            placeholder="Select staff to exclude"
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Exclude staff',
            }}
          />
        )}
      />

      {showCompletionRate && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Happiness-survey completion rate (%)
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Min %"
              size="small"
              type="number"
              value={value.completionRateMinPercent ?? ''}
              onChange={handleRateChange('completionRateMinPercent')}
              onBlur={handleRateBlur('completionRateMinPercent')}
              inputProps={{ min: 0, max: 100, step: 1, 'aria-label': 'Completion rate minimum percent' }}
              sx={{ maxWidth: 140 }}
            />
            <TextField
              label="Max %"
              size="small"
              type="number"
              value={value.completionRateMaxPercent ?? ''}
              onChange={handleRateChange('completionRateMaxPercent')}
              onBlur={handleRateBlur('completionRateMaxPercent')}
              inputProps={{ min: 0, max: 100, step: 1, 'aria-label': 'Completion rate maximum percent' }}
              sx={{ maxWidth: 140 }}
            />
          </Stack>
        </Box>
      )}

      <Box>
        <Typography variant="caption" color="text.secondary">
          A recipient must match ALL filter types you set (intersection). Within each type, ANY match counts (union). Excluded staff never receive the notification.
        </Typography>
      </Box>

      {isEmpty && (
        <Alert severity="warning" role="alert">
          No audience filter — all matching users will receive this notification.
        </Alert>
      )}
    </Stack>
  );
}

export default AudienceFilterSelector;
